/**
 * @fileoverview Componente TextSearchMC optimizado para búsquedas con debounce y performance en grandes volúmenes.
 * Implementa arquitectura Atomic Design (atom), separación estricta de lógica UI, y optimización de renderizado con React.memo, useCallback y useMemo.
 * @module TextSearchMC
 * @category Component - Atom
 * @since 1.0.0
 * @version 2.0.0 - JSDoc completo, optimización de renderizado y control de valor
 */
import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
  memo
} from 'react';
import TextIcon from '../../../../components/icons/SearchIcon';

/**
 * Componente atómico optimizado para búsquedas con debounce y control de valor.
 *
 * - Controla el valor del input internamente y sincroniza con defaultValue externo
 * - Aplica debounce para evitar llamadas excesivas a onChange
 * - Optimizado con React.memo, useCallback y useMemo para evitar renders innecesarios
 * - Cumple con Atomic Design (atom) y arquitectura Mercately
 *
 * @function TextSearchMC
 * @category Component - Atom
 *
 * @param {TextSearchMCProps} props - Propiedades del componente
 * @returns {JSX.Element} Input de búsqueda optimizado
 *
 * @example
 * <TextSearchMC
 *   placeholder="Buscar cliente..."
 *   defaultValue=""
 *   debounceTime={500}
 *   onChange={(val) => console.log(val)}
 * />
 *
 * @since 1.0.0
 * @version 2.0.0 - JSDoc completo y optimización
 */
const TextSearchMC = memo(({
  placeholder = 'Buscar...',
  defaultValue = '',
  debounceTime = 2000,
  inputClassName = '',
  mainContainerClassName = '',
  onChange
}) => {
  const inputRef = useRef(null);
  const [value, setValue] = useState(defaultValue);
  const [isInitializedComponent, setIsInitializedComponent] = useState(false);
  const [debouncedValue, setDebouncedValue] = useState(defaultValue);

  // Actualizar el estado interno cuando defaultValue cambia desde el padre
  useEffect(() => {
    setValue(defaultValue);
    setDebouncedValue(defaultValue);
  }, [defaultValue]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, debounceTime);

    return () => {
      clearTimeout(handler);
    };
  }, [value, debounceTime]);

  useEffect(() => {
    setIsInitializedComponent(true);
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    if (onChange && isInitializedComponent) {
      onChange(debouncedValue);
    }
  }, [debouncedValue, onChange, isInitializedComponent]);

  const handleInputChange = useCallback((e) => {
    setValue(e.target.value);
  }, []);

  const mainContainerClass = useMemo(() => [
    'tw-relative',
    'tw-w-full',
    'tw-flex',
    mainContainerClassName || ''
  ].join(' '), [mainContainerClassName]);

  const inputClasses = useMemo(
    () => `tw-w-full tw-pl-10 tw-pr-4 tw-py-1 tw-border tw-border-gray-200 tw-rounded-xl tw-bg-gray-50 tw-placeholder-gray-500 tw-max-h-12 tw-text-default ${inputClassName}`,
    [inputClassName]
  );

  return (
    <div className={mainContainerClass}>
      <span className="tw-absolute tw-inset-y-0 tw-left-0 tw-flex tw-items-center tw-pl-4 tw-text-gray-400">
        <TextIcon />
      </span>
      <input
        ref={inputRef}
        type="text"
        placeholder={placeholder}
        value={value}
        onChange={handleInputChange}
        className={inputClasses}
      />
    </div>
  );
});

TextSearchMC.displayName = 'TextSearchMC';

export default TextSearchMC;
